/**
 * FilterModal.tsx — the query editor shell.
 *
 * Hosts the coresignal-lab-style BlockEditor: semantic cards (Work Experience / Education / Skills /
 * Languages / person-field / advanced group) that compile to our `Group/Filter` tree. Edits the canonical
 * tree directly via onChange; Search commits by running, close just hides.
 */

import { X, Search, Loader2 } from 'lucide-react';
import type { Group } from '@/lib/talentPool.api';
import { BlockEditor } from './blocks/BlockEditor';

export function FilterModal({
    tree,
    onChange,
    count,
    counting,
    searching,
    onClose,
    onSearch,
}: {
    tree: Group;
    onChange: (t: Group) => void;
    count: number | null;
    counting: boolean;
    searching: boolean;
    onClose: () => void;
    onSearch: () => void;
}) {
    return (
        <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/50 p-4" onMouseDown={onClose}>
            <div className="flex h-[88vh] max-h-[900px] w-[1120px] max-w-[97vw] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl" onMouseDown={(e) => e.stopPropagation()}>
                {/* header */}
                <div className="flex h-14 shrink-0 items-center gap-3 border-b border-zinc-200 bg-white px-5">
                    <span className="text-[15px] font-semibold text-zinc-900">Edit filters</span>
                    <div className="flex-1" />
                    <span className="text-[13px] text-zinc-500">
                        {counting ? 'counting…' : count == null ? '—' : (<><b className="text-[#B23A22]">{count.toLocaleString()}</b> matches</>)}
                    </span>
                    <button
                        type="button"
                        onClick={onSearch}
                        disabled={searching}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-[#0d1830] px-4 py-2 text-[13px] font-medium text-white hover:bg-[#1c2b4d] disabled:opacity-50"
                    >
                        {searching ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
                        Search
                    </button>
                    <button type="button" onClick={onClose} className="rounded-md p-1 text-zinc-400 hover:text-zinc-700" aria-label="Close">
                        <X size={18} />
                    </button>
                </div>

                {/* body */}
                <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
                    <div className="mx-auto max-w-[720px]">
                        <BlockEditor tree={tree} onChange={onChange} />
                    </div>
                </div>
            </div>
        </div>
    );
}
